import { useEffect, useRef, useState, useCallback } from 'react';
import { Tool } from '../types';
import './ToolPanel.css';

/** 面板最小尺寸（默认值即为最小限制） */
const MIN_WIDTH = 640;
const MIN_HEIGHT = 400;

interface ToolPanelProps {
    tool: Tool;
    onClose: () => void;
}

const ToolPanel = ({ tool, onClose }: ToolPanelProps) => {
    const ToolComponent = tool.component;
    const modalRef = useRef<HTMLDivElement>(null);

    // 面板尺寸状态：null 表示使用默认 CSS 尺寸
    const [panelSize, setPanelSize] = useState<{ width: number; height: number } | null>(null);
    const [isResizing, setIsResizing] = useState(false);

    useEffect(() => {
        const handleKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
        };
        document.addEventListener('keydown', handleKey);
        document.body.style.overflow = 'hidden';
        return () => {
            document.removeEventListener('keydown', handleKey);
            document.body.style.overflow = '';
            // 防止拖拽中途关闭面板导致 body 样式残留
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };
    }, [onClose]);

    /** 开始拖拽调整大小 */
    const handleResizeStart = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const modal = modalRef.current;
        if (!modal) return;

        // 记录当前实际尺寸作为起始值
        const rect = modal.getBoundingClientRect();
        const startWidth = rect.width;
        const startHeight = rect.height;
        const startX = e.clientX;
        const startY = e.clientY;

        setIsResizing(true);

        const handleMouseMove = (moveEvent: MouseEvent) => {
            const deltaX = moveEvent.clientX - startX;
            const deltaY = moveEvent.clientY - startY;

            const newWidth = Math.max(MIN_WIDTH, startWidth + deltaX);
            const newHeight = Math.max(MIN_HEIGHT, startHeight + deltaY);

            setPanelSize({ width: newWidth, height: newHeight });
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            // 恢复文本选择
            document.body.style.userSelect = '';
            document.body.style.cursor = '';
        };

        // 拖拽时禁止文本选择
        document.body.style.userSelect = 'none';
        document.body.style.cursor = 'nwse-resize';
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, []);

    return (
        <div className={`tool-overlay ${isResizing ? 'tool-overlay--resizing' : ''}`} onClick={onClose}>
            <div
                ref={modalRef}
                className="tool-modal"
                role="dialog"
                aria-modal="true"
                aria-label={tool.title}
                onClick={(e) => e.stopPropagation()}
                style={
                    panelSize
                        ? { width: panelSize.width, height: panelSize.height, maxWidth: 'none', maxHeight: 'none' }
                        : undefined
                }
            >
                <div className="tool-modal-header">
                    <div className="tool-modal-heading">
                        <span className="tool-modal-icon" style={{ background: tool.color }}>
                            {tool.icon}
                        </span>
                        <div className="tool-modal-titles">
                            <h2 className="tool-modal-title">{tool.title}</h2>
                            <p className="tool-modal-desc">{tool.description}</p>
                        </div>
                    </div>
                    <button className="close-button" onClick={onClose} aria-label="关闭">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
                <div className="tool-modal-content">
                    <ToolComponent />
                </div>
                {/* 右下角拖拽调整大小手柄 */}
                <div
                    className="tool-modal-resize-handle"
                    onMouseDown={handleResizeStart}
                    aria-label="拖拽调整面板大小"
                    title="拖拽可放大面板"
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" opacity="0.4">
                        <path d="M22 22H20V20H22V22ZM22 18H18V22H16V16H22V18ZM22 14H14V22H12V12H22V14ZM22 10H10V22H8V8H22V10Z" />
                    </svg>
                </div>
            </div>
        </div>
    );
};

export default ToolPanel;
